import { useState } from "react";
import { restockOne, stockLevel, type StockItem } from "./stock";
import { withSpan } from "./otel";
import { signIn } from "./session";

type Phase = { name: "idle" } | { name: "restocking" } | { name: "error"; message: string };

const LABEL: Record<ReturnType<typeof stockLevel>, string> = {
  out: "out of stock",
  low: "low",
  ok: "in stock",
};

export function StockRow({
  item,
  onRestocked,
}: {
  item: StockItem;
  onRestocked: (sku: string, quantityOnHand: number) => void;
}) {
  const [phase, setPhase] = useState<Phase>({ name: "idle" });
  const level = stockLevel(item);

  async function restock() {
    setPhase({ name: "restocking" });
    const result = await withSpan("stock.restock", { "catalog.sku": item.sku }, () => restockOne(item.sku));

    if (result.kind === "ok") {
      setPhase({ name: "idle" });
      const restocked = result.value.items[0];
      if (restocked) onRestocked(item.sku, restocked.quantity);
    } else if (result.kind === "unauthorized") {
      signIn(window.location.pathname);
    } else {
      setPhase({ name: "error", message: result.message });
    }
  }

  return (
    <>
      <tr className="stock-row">
        <td>
          <code>{item.sku}</code>
        </td>
        <td>{item.name ?? "—"}</td>
        <td className="stock-num">{item.quantityOnHand.toLocaleString()}</td>
        <td>
          <div className="stock-bar">
            <div className={`stock-bar-fill ${level}`} style={barWidth(item)} />
          </div>
          <span className="stock-bar-label">
            {item.initialStock ? `${item.quantityOnHand} / ${item.initialStock}` : "—"}
          </span>
        </td>
        <td>
          <span className={`stock-badge ${level}`}>{LABEL[level]}</span>
        </td>
        <td>
          <button type="button" onClick={restock} disabled={phase.name === "restocking"}>
            {phase.name === "restocking" ? "Restocking…" : "Restock"}
          </button>
        </td>
      </tr>
      {phase.name === "error" && (
        <tr className="stock-row-error-row">
          <td colSpan={6}>
            <p className="field-error">Couldn't restock {item.sku}: {phase.message}</p>
          </td>
        </tr>
      )}
    </>
  );
}

function barWidth(item: StockItem): { width: string } {
  if (!item.initialStock) return { width: "0%" };
  const pct = Math.min(100, Math.max(0, (item.quantityOnHand / item.initialStock) * 100));
  return { width: `${pct}%` };
}
